/**
 * Iter123 — County Assignment cascading dropdowns.
 *
 * State → City → County (dependent) using bundled US Cities/Counties data.
 * On save, emits { state, city, county, latitude, longitude } to the parent.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin } from 'lucide-react';
import usLocations from '../data/us_locations.json';

export default function CountyAssignmentPicker({ value, onChange, disabled = false }) {
  const [state, setState] = useState(value?.state || '');
  const [city, setCity] = useState(value?.city || '');
  const [county, setCounty] = useState(value?.county || '');

  const stateOpt = useMemo(
    () => (usLocations.states || []).find(s => s.code === state || s.name === state),
    [state],
  );

  const cityOpt = useMemo(
    () => (stateOpt?.cities || []).find(c => c.name === city),
    [stateOpt, city],
  );

  // When state changes, reset city/county
  useEffect(() => {
    if (city && stateOpt && !stateOpt.cities.some(c => c.name === city)) {
      setCity(''); setCounty('');
    }
  }, [state, stateOpt, city]);

  // When city changes, auto-select its county
  useEffect(() => {
    if (cityOpt && cityOpt.county !== county) setCounty(cityOpt.county);
  }, [cityOpt]); // eslint-disable-line

  // Emit change
  useEffect(() => {
    if (!onChange) return;
    if (!state) {
      onChange({ state: '', city: '', county: '', latitude: null, longitude: null });
      return;
    }
    const countyOpt = (stateOpt?.counties || []).find(c => c.name === county);
    onChange({
      state,
      state_name: stateOpt?.name || state,
      city: city || '',
      county: county || '',
      latitude: cityOpt?.lat ?? countyOpt?.lat ?? null,
      longitude: cityOpt?.lng ?? countyOpt?.lng ?? null,
    });
  }, [state, city, county]); // eslint-disable-line

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="county-assignment-picker">
      <div>
        <label className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> State</label>
        <Select value={state} onValueChange={setState} disabled={disabled}>
          <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="county-picker-state"><SelectValue placeholder="Select state" /></SelectTrigger>
          <SelectContent>
            {(usLocations.states || []).map(s => (
              <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-slate-400">City</label>
        <Select value={city} onValueChange={setCity} disabled={disabled || !state}>
          <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="county-picker-city"><SelectValue placeholder={state ? 'Select city' : 'Choose state first'} /></SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {(stateOpt?.cities || []).map(c => (
              <SelectItem key={`${c.name}-${c.county}`} value={c.name}>{c.name} <span className="text-slate-400 text-[10px] ml-1">· {c.county}</span></SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-slate-400">County</label>
        <Select value={county} onValueChange={setCounty} disabled={disabled || !state}>
          <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="county-picker-county"><SelectValue placeholder={state ? 'Select county' : 'Choose state first'} /></SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {(stateOpt?.counties || []).map(c => (
              <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
